import { useIntl, Button } from '@mfw/components';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

import type { ApiUserMembership } from '../../api/types/memberships.types';

import clientProfileMessages from '../../pages/Profiles/OrganizationClientProfile/messages';
import { totalsByCurrency } from './membershipsAwaitingCard';
import { formatCurrencyMajor } from '../../utils';

interface MembershipActivateRemainingDialogProps {
  memberships: ApiUserMembership[];
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * The card is on the client's account, not on one membership: once it is
 * linked, the client's other memberships still waiting for it (same
 * organization) can start too. They are named with what they cost and
 * nothing is charged before the client confirms.
 */
export const MembershipActivateRemainingDialog = ({
  memberships,
  onClose,
  onConfirm,
}: MembershipActivateRemainingDialogProps) => {
  const { formatMessage, locale } = useIntl();

  return (
    <Dialog
      data-testid="membership-activate-remaining"
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      open
    >
      <Box p={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          {formatMessage(clientProfileMessages.activateRemainingTitle, {
            count: memberships.length,
          })}
        </Typography>
        <Typography color="text.secondary" variant="body2" mb={2}>
          {formatMessage(clientProfileMessages.activateRemainingDescription)}
        </Typography>

        <Stack spacing={1}>
          {memberships.map((cm) => (
            <Box
              data-testid="membership-activate-remaining-item"
              justifyContent="space-between"
              display="flex"
              key={cm.id}
              gap={2}
            >
              <Typography variant="body2">{cm.membership?.name}</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                {formatCurrencyMajor(cm.finalPrice, cm.currency, locale)}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Typography variant="body2" fontWeight={700} mt={2}>
          {formatMessage(clientProfileMessages.activateRemainingTotal, {
            total: totalsByCurrency(memberships)
              .map(({ amount, currency }) =>
                formatCurrencyMajor(amount, currency, locale),
              )
              .join(' + '),
          })}
        </Typography>

        <Box justifyContent="flex-end" display="flex" gap={1} mt={3}>
          <Button
            data-testid="membership-activate-remaining-dismiss"
            variant="outlined"
            onClick={onClose}
            size="small"
          >
            {formatMessage(clientProfileMessages.activateRemainingDismiss)}
          </Button>
          <Button
            data-testid="membership-activate-remaining-confirm"
            variant="contained"
            onClick={onConfirm}
            size="small"
          >
            {formatMessage(clientProfileMessages.activateRemainingConfirm)}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
